import React from 'react';
import style from '../ThankYouScreen.module.scss';
import { Body, Card, CardContent, CardMedia, Heading } from '@walmart-web/livingdesign-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';
import { selfScheduleEventCodeMap } from 'config/const';
import { useCommonOAuthRedirect } from '../../../hooks/useCommonOAuthRedirect';
import RedirectMessage from '../../../components/RedirectMessage';
import { formatPhoneNumber, resolveStoreName } from '../utils/formatUtils';

const Icon = require('@livingdesign/icons');

const formatHiringManagerName = (name) => {
	if (!name) {
		return '';
	}
	return name
		.toLowerCase()
		.split(' ')
		.filter(Boolean)
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(' ');
};

const ThankYouScreen = () => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery({ maxWidth: 650 });
	const selfSchedule = useSelector((state) => state.selfSchedule);
	const global = useSelector((state) => state.global);
	
	const { showContinueButton, handleContinueRedirect } = useCommonOAuthRedirect({
		type: 'CP_SELF_SCHEDULE',
		status: 'SUCCESS',
		message: 'Interview/orientation/tour scheduled successfully'
	});


	let startDate = selfSchedule.isSelfScheduleSC
		? selfSchedule.confirmedSlot.startTime.slice(0, 10)
		: moment(selfSchedule.confirmedSlot.date, 'dddd, MM-DD-YYYY', true);
	let startTime = selfSchedule.isSelfScheduleSC
		? moment(selfSchedule.confirmedSlot.startTime.slice(11), 'HH:mm:ss').format('LT')
		: moment(selfSchedule.confirmedSlot.startTime).format('LT');

	if (
		!selfSchedule.isDirectlyThankYouScreen &&
		(selfSchedule.eventCode === selfScheduleEventCodeMap.SC_ORIENTATION_EVENT_CODE_1 ||
			selfSchedule.eventCode === selfScheduleEventCodeMap.SC_ORIENTATION_EVENT_CODE_2)
	) {
		startDate = moment(selfSchedule.confirmedSlot.date, 'MM-DD-YYYY', true);
		startTime = selfSchedule.confirmedSlot.startTime;
	}
	const formattedPhoneNumber = formatPhoneNumber(selfSchedule.phoneNumber);
	const formattedStorePhone = formatPhoneNumber(selfSchedule.storePhoneNumber);
	const isJobPreviewCandidate = !!selfSchedule.isJobPreviewCandidate;
	const isJobPreviewReschedule = isJobPreviewCandidate && !!selfSchedule.isReschedule;
	const isPrescreenEvent =
		selfSchedule.eventCode === selfScheduleEventCodeMap.SH_PRESCREEN_EVENT_CODE;
	const hiringManagerName = formatHiringManagerName(selfSchedule.hiringManagerName);
	const storeNbr = selfSchedule.storeNbr || selfSchedule.responseData?.storeNbr;
	const storeName = resolveStoreName({ storeNbr, storeName: selfSchedule.storeName, t });
	const jobPreviewDateTime = `${moment(startDate)
		.locale(global.language === 'ES' ? 'es' : 'en')
		.format('ddd MMM D, YYYY')}, ${moment(startTime, ['LT', 'h:mma', 'hh:mm A']).format('h:mma')}`;
	return (
		<div className={style.thankyou_container}>
			<div className={style.thankyou_screen_parent}>
				<div className={style.thankyou_top_layout}></div>
			</div>
			<div className={style.thankyou_bottom_layout}>
				<div style={{ padding: '16px 0' }}>
					<Heading
						as="h1"
						size="medium"
						weight={700}
						UNSAFE_style={{ color: '#FFFF' }}
						UNSAFE_className={style.thankyou_text}>
						{t(
							isJobPreviewReschedule
								? 'selfSchedule.youreRescheduled'
								: isJobPreviewCandidate
								? 'selfSchedule.youreScheduled'
								: 'selfSchedule.thankYou'
						)}
					</Heading>
					{!isPrescreenEvent ? (
						<Body
							as="p"
							size="medium"
							weight={400}
							UNSAFE_style={{ color: '#FFFF' }}
							UNSAFE_className={style.thankyou_subtext}>
							{t('selfSchedule.thankYou1SC')}
						</Body>
					) : (
						<Body
							as="p"
							size="medium"
							weight={400}
							UNSAFE_style={{ color: '#FFFF' }}
							UNSAFE_className={style.thankyou_subtext}>
							{t(
								isJobPreviewReschedule
									? 'selfSchedule.jobPreviewHasBeenUpdated'
									: isJobPreviewCandidate
									? 'selfSchedule.jobPreviewHasBeenScheduled'
									: 'selfSchedule.thankYou1'
							)}
						</Body>
					)}
				</div>
				<Card size="large">
					<CardMedia>
						<img
							src={
								isMobile
									? require('../../../../src/assets/images/celebration_mobile.png')
									: require('../../../../src/assets/images/celebration_rectangle.png')
							}
							alt="Celebration"
							className={style.celebration_img}
							width="100%"
						/>
					</CardMedia>
					<CardContent className={style.card_Item}>
						<div className={style.thankyou_screen_content}>
							{isJobPreviewCandidate ? (
								<div className={style.jobPreview_confirmation}>
									<div className={style.jobPreview_detail_row}>
										<Icon.Calendar size="medium" />
										<div>{jobPreviewDateTime}</div>
									</div>
									<div className={style.jobPreview_detail_row}>
										<Icon.Location size="medium" />
										<div>
											{storeName ? <div>{storeName}</div> : null}
											<div>{selfSchedule.storeAddress}</div>
											{formattedStorePhone ? <div>{formattedStorePhone}</div> : null}
										</div>
									</div>
									{hiringManagerName ? (
										<div className={style.jobPreview_detail_row}>
											<Icon.User size="medium" />
											<div>
												<div>{hiringManagerName}</div>
												<div className={style.jobPreview_manager_title}>
													{t('selfSchedule.hiringManagerTitle')}
												</div>
											</div>
										</div>
									) : null}
									<p>{t('selfSchedule.jobPreviewCheckMessages')}</p>
									<p>
										{hiringManagerName
											? t('selfSchedule.jobPreviewArriveWithManager', {
													hiringManager: hiringManagerName
											  })
											: t('selfSchedule.jobPreviewArrive')}
									</p>
									<p>{t('selfSchedule.jobPreviewAbout')}</p>
								</div>
							) : (
								<>
									<div className={style.thankyou_screen_info}>
										<strong>{`${moment(startDate)
											.locale(global.language === 'ES' ? 'es' : 'en')
											.format('dddd, MM-DD-YYYY')} @ ${startTime} (${t(
											selfSchedule.duration
										)})`}</strong>
									</div>
									{!isPrescreenEvent ? (
										<div className={style.thankyou_screen_content_sc}>
											<br />
											<strong>{t('selfSchedule.at')}</strong>
											<br />
											<strong>{selfSchedule.storeAddress}</strong>
										</div>
									) : (
										<p>{`${t('selfSchedule.NoStoreVisit')} ${formattedPhoneNumber}!`}</p>
									)}
									<div className={style.thankyou_screen_footer_text}>
										{t('selfSchedule.thankYou2')}
									</div>
								</>
							)}
							<RedirectMessage
								showContinueButton={showContinueButton}
								handleContinueRedirect={handleContinueRedirect}
								buttonText={t('general.continue')}
								containerStyle={{
									marginTop: '10px',
									backgroundColor: '#f8f9fa',
									borderRadius: '8px',
									boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
									alignItems: 'stretch',
									width: '100%'
								}}
								buttonStyle={{
									padding: '12px 32px',
									fontSize: '16px',
									fontWeight: '700',
									borderRadius: '6px',
									minWidth: '160px',
									backgroundColor: '#0071ce',
									color: '#FFFFFF',
									boxShadow: '0 3px 6px rgba(0,113,206,0.3)',
									textTransform: 'uppercase',
									letterSpacing: '0.5px',
									width: '100%'
								}}
							/>
						</div>
					</CardContent>
				</Card>
			</div>
		</div>
	);
};

export default ThankYouScreen;
